import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import { Flag, Info, Pencil, TriangleAlert } from 'lucide-react';
import { useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import { CampoSelecao, CampoTexto } from '@/componentes/Campo';
import { CampoDoMotivo } from '@/componentes/CampoDoMotivo';
import { Barra, Carregando } from '@/componentes/Esqueleto';
import { ErroDaLista } from '@/componentes/ErroDaLista';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { Janela } from '@/componentes/JanelaDeAcao';
import { PaginaSemPermissao } from '@/componentes/SemPermissao';
import { RodapeDeContagem, Tabela } from '@/componentes/Tabela';
import { formatarCompetencia, formatarReais, interpretarNumero } from '@/lib/formato';
import { motivoValido } from '@/lib/motivo';
import { explicarFalha } from '@/lib/pedido';
import { retornoDe } from '@/lib/retorno';
import { useSessao } from '@/modulos/sessao/consultas';

import { apiDaRenovacao, type Alternativa, type Estudo, type PremissaDoEstudo, type ResultadoDaAlternativa } from './api';
import { JanelaDaProposta } from './JanelaDaProposta';
import {
  codigoDaProposta,
  codigoDoEstudo,
  diaOuTravessao,
  GRUPOS_DE_PREMISSA,
  horas,
  idadeEscrita,
  janelaEscrita,
  NOME_DA_ORIGEM,
  percentual,
  premissaEscrita,
  reaisPorHora,
  detalheDaConclusao,
  detalheDaPremissa,
  tituloDaPremissa,
  textoDaTendencia,
  TIPO_DA_ALTERNATIVA,
} from './renovacao';
import { SeloDaProposta, SeloDeCandidata, SeloDoEstudo } from './Selo';

const rota = getRouteApi('/equipamentos/renovacao/estudos/$estudoId');

type Acao = 'concluir' | 'arquivar' | 'propor' | 'orcamento' | null;

/**
 * O ESTUDO DE REFORMA OU SUBSTITUIÇÃO (ferramenta 3 do v6; 0107): a máquina
 * hoje, as alternativas lado a lado — cada uma com o custo por hora, o total,
 * o valor presente e a indisponibilidade, ou "não calculada: falta X" —, e as
 * premissas em seções, cada uma com a fonte e a origem.
 *
 * A recomendação é da pessoa: concluir escolhe a alternativa e escreve o
 * porquê, e os números ficam congelados. A marca de candidata é A1, só com os
 * dados completos. Sem desenho no manual: segue o formulário em seções (p.39)
 * e a comparação em cartões lado a lado; dúvida de design registrada.
 */
export function PaginaEstudo() {
  const { estudoId } = rota.useParams();
  const sessao = useSessao();
  const permissoes = sessao.data?.permissoes ?? [];
  const pode = permissoes.includes('renovacao.ler');
  const grava = permissoes.includes('estudo.gravar');
  const propoe = permissoes.includes('investimento.propor');
  const [acao, setAcao] = useState<Acao>(null);
  const [editando, setEditando] = useState<PremissaDoEstudo | null>(null);
  const consulta = useQuery({ queryKey: ['renovacao', 'estudo', estudoId], queryFn: () => apiDaRenovacao.estudo(estudoId), enabled: pode, meta: { oQueLia: 'o estudo de renovação' } });
  const e = consulta.data;

  if (sessao.data && !pode) {
    return <PaginaSemPermissao sobretitulo="Frota › Renovação" titulo="Estudo de renovação" modulo="a renovação da frota" licenca="renovacao.ler" />;
  }

  const rascunho = e?.estudo.situacao === 'rascunho';
  const semProposta = (e?.propostas.length ?? 0) === 0;
  const primario = grava && e?.podeGravar ? 'concluir' : propoe && e?.podePropor && semProposta ? 'propor' : null;

  return (
    <div className="min-w-0 flex-1 overflow-auto">
      <div className="pagina">
        <CabecalhoPagina
          sobretitulo="Frota › Renovação"
          titulo={e ? `${codigoDoEstudo(e.estudo.numero)} · ${e.estudo.codigo}` : 'Estudo de renovação'}
          subtitulo={e ? `${e.estudo.descricao} · data-base ${diaOuTravessao(e.estudo.dataBase)} · horizonte de ${String(e.estudo.horizonteMeses)} meses` : 'A comparação das alternativas com as premissas à vista'}
          acoes={
            <>
              <Link to="/equipamentos/renovacao" className="botao botao-secundario">Voltar à renovação</Link>
              {grava && e && rascunho && e.podeGravar && e.orcamento.permitido ? <Botao nivel="secundario" onClick={() => { setAcao('orcamento'); }}>Puxar do orçamento</Botao> : null}
              {grava && e && rascunho && e.podeGravar ? <Botao nivel="fantasma" onClick={() => { setAcao('arquivar'); }}>Arquivar estudo</Botao> : null}
              {primario === 'concluir' ? <Botao nivel="primario" onClick={() => { setAcao('concluir'); }}>Concluir estudo</Botao> : null}
              {primario === 'propor' ? <Botao nivel="primario" onClick={() => { setAcao('propor'); }}>Propor investimento</Botao> : null}
            </>
          }
        />

        {consulta.isError && !e ? (
          <ErroDaLista oQue="Os números do estudo" erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} />
        ) : !e ? (
          <Carregando oQue="Carregando o estudo">
            <div className="flex flex-col gap-4">
              <Barra className="h-24 w-full rounded-card" />
              <div className="grid gap-4 md:grid-cols-3">{[1, 2, 3].map((n) => <Barra key={n} className="h-48 rounded-card" />)}</div>
            </div>
          </Carregando>
        ) : (
          <>
            <Situacao e={e} />
            <MaquinaHoje e={e} />
            <section className="flex flex-col gap-3" aria-label="As alternativas lado a lado">
              <h2 className="txt-secao text-tinta">Quanto custa cada alternativa no horizonte?</h2>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {e.numeros.alternativas.map((a) => <CartaoDaAlternativa key={a.codigo} a={a} escolhida={e.estudo.alternativaEscolhida === a.codigo} />)}
              </div>
              {e.numeros.naoInclui.length > 0 ? (
                <div className="cartao flex flex-col gap-2 p-4">
                  <h3 className="txt-corpo font-semibold text-tinta">O que ficou de fora</h3>
                  <ul className="txt-apoio flex list-disc flex-col gap-1 pl-5 text-pretty text-tinta-secundaria">{e.numeros.naoInclui.map((x) => <li key={x}>{x}</li>)}</ul>
                </div>
              ) : null}
            </section>
            <Premissas e={e} aoEditar={grava ? setEditando : null} />
            <SerieMensal e={e} />
          </>
        )}
      </div>
      {grava && e && e.podeGravar ? (
        <>
          <JanelaDaConclusao e={e} aberta={acao === 'concluir'} aoFechar={() => { setAcao(null); }} />
          <JanelaDoArquivo e={e} aberta={acao === 'arquivar'} aoFechar={() => { setAcao(null); }} />
          <JanelaDoOrcamento e={e} aberta={acao === 'orcamento'} aoFechar={() => { setAcao(null); }} />
          {editando ? <JanelaDaPremissa e={e} p={editando} aoFechar={() => { setEditando(null); }} /> : null}
        </>
      ) : null}
      {propoe && e && e.podePropor ? (
        <JanelaDaProposta
          key={e.estudo.id}
          aberta={acao === 'propor'}
          aoFechar={() => { setAcao(null); }}
          inicio={{
            tipo: TIPO_DA_ALTERNATIVA[e.estudo.alternativaEscolhida ?? 'manter'] ?? 'substituir_comprando',
            equipamentoId: e.estudo.equipamentoId,
            estudoId: e.estudo.id,
            descricao: `${e.estudo.nomeDaAlternativa ?? ''} ${e.estudo.codigo}`.trim(),
            investimento: valorDaPremissa(e, e.estudo.alternativaEscolhida === 'reformar' ? 'custo_da_reforma' : 'preco_da_nova'),
          }}
        />
      ) : null}
    </div>
  );
}

const valorDaPremissa = (e: Estudo, codigo: string) => e.premissas.find((p) => p.codigo === codigo)?.valor ?? null;

function Situacao({ e }: { readonly e: Estudo }) {
  const c = e.numeros.candidata;
  return (
    <section className="flex flex-col gap-3" aria-label="A situação do estudo">
      <div className="flex flex-wrap items-center gap-2">
        <SeloDoEstudo situacao={e.estudo.situacao} />
        {c.marcada ? <SeloDeCandidata /> : null}
        {e.propostas.map((p) => (
          <span key={p.id} className="flex items-center gap-1.5">
            <span className="txt-apoio text-tinta-secundaria">{codigoDaProposta(p.numero)}</span>
            <SeloDaProposta situacao={p.situacao} />
          </span>
        ))}
      </div>
      {e.congelado ? (
        <div className="cartao flex flex-col gap-2 p-4">
          <p className="txt-corpo font-semibold text-tinta">{`Recomendação: ${e.estudo.nomeDaAlternativa ?? ''}`}</p>
          <p className="txt-corpo text-pretty text-tinta">{e.estudo.conclusao}</p>
          <p className="txt-apoio text-tinta-secundaria">{`${e.frases.concluido} Concluído por ${e.estudo.concluidoPor ?? 'quem tinha a licença'} em ${diaOuTravessao(e.estudo.concluidoEm)}, com a conta ${e.numeros.versaoDaConta}.`}</p>
        </div>
      ) : null}
      {e.estudo.situacao === 'arquivado' ? <p className="txt-apoio text-tinta-secundaria">{`Arquivado em ${diaOuTravessao(e.estudo.arquivadoEm)}: ${e.estudo.motivoDoArquivo ?? ''}`}</p> : null}
      <div className={`flex items-start gap-3 rounded-controle p-4 ${c.marcada ? 'bg-atencao-fundo' : 'bloco-rebaixado'}`}>
        {c.marcada ? <Flag aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-atencao" /> : <Info aria-hidden="true" className="mt-0.5 size-5 shrink-0 text-informacao" />}
        <div className="flex flex-col gap-1">
          <p className="txt-corpo text-pretty text-tinta">{c.motivo}</p>
          <p className="txt-apoio text-pretty text-tinta-secundaria">{e.frases.candidata}</p>
        </div>
      </div>
    </section>
  );
}

function MaquinaHoje({ e }: { readonly e: Estudo }) {
  const n = e.numeros;
  const b = n.economia;
  const vc = n.valorContabil;
  const itens: readonly (readonly [string, string])[] = [
    ['Custo total por hora', b.horasInsuficientes ? 'horas insuficientes' : reaisPorHora(b.custoTotalPorHora)],
    ['Horas produtivas na janela', `${horas(b.horasProdutivas)} em ${String(b.meses)} meses`],
    ['Operação e manutenção por hora', reaisPorHora(b.omPorHora)],
    ['Combustível por hora', reaisPorHora(b.combustivelPorHora)],
    ['Manutenção por hora', reaisPorHora(b.manutencaoPorHora)],
    ['Tendência da manutenção', textoDaTendencia(n.tendencia)],
    ['Margem por hora', reaisPorHora(b.margemPorHora)],
    ['Disponibilidade', percentual(b.disponibilidade)],
    ['Valor contábil gerencial', vc.valor === null ? 'sem depreciação cadastrada' : formatarReais(vc.valor)],
    ['Vida consumida', percentual(vc.vidaConsumida)],
    ['Idade e horímetro', `${e.idade ? idadeEscrita(e.idade) : '—'} · ${e.horimetro === null ? 'sem leitura' : horas(e.horimetro)}`],
    ['MTBF e MTTR (módulo 18)', e.confiabilidade ? `${e.confiabilidade.mtbf.valor === null ? 'sem base' : horas(e.confiabilidade.mtbf.valor)} · ${e.confiabilidade.mttr.valor === null ? 'sem base' : horas(e.confiabilidade.mttr.valor)}` : 'congelado sem o 18'],
  ];
  return (
    <section className="cartao flex flex-col gap-4 p-6" aria-label="A máquina hoje">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="txt-secao text-tinta">Como a máquina está hoje?</h2>
        <span className="txt-apoio text-tinta-secundaria">{`Janela de ${janelaEscrita(b.de, b.ate)}`}</span>
      </div>
      <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2 xl:grid-cols-4">
        {itens.map(([rotulo, valor]) => (
          <div key={rotulo} className="flex min-w-0 flex-col gap-0.5">
            <dt className="txt-apoio text-tinta-secundaria">{rotulo}</dt>
            <dd className="txt-corpo tabular-nums text-tinta">{valor}</dd>
          </div>
        ))}
      </dl>
      <p className="txt-apoio flex items-start gap-2 text-pretty text-tinta-secundaria">
        <Info aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-informacao" />
        {e.frases.valor}
      </p>
    </section>
  );
}

function CartaoDaAlternativa({ a, escolhida }: { readonly a: ResultadoDaAlternativa; readonly escolhida: boolean }) {
  return (
    <article className={`cartao flex flex-col gap-3 p-4 ${escolhida ? 'ring-2 ring-marca' : ''}`} aria-label={a.nome}>
      <div className="flex items-start justify-between gap-2">
        <h3 className="txt-subtitulo text-tinta">{a.nome}</h3>
        {escolhida ? <span className="etiqueta">Recomendada no estudo</span> : null}
      </div>
      {a.calculada ? (
        <>
          <div className="flex flex-col">
            <span className="txt-kpi numero text-tinta">{a.custoPorHora === null ? formatarReais(a.total ?? 0) : reaisPorHora(a.custoPorHora)}</span>
            <span className="txt-apoio text-tinta-secundaria">{a.custoPorHora === null ? 'custo no horizonte (não produz hora)' : `por hora produtiva · ${formatarReais(a.total ?? 0)} no horizonte`}</span>
          </div>
          <dl className="txt-apoio grid grid-cols-[1fr_auto] gap-x-3 gap-y-1">
            {a.partes.map((p) => (
              <div key={p.rotulo} className="contents">
                <dt className="text-tinta-secundaria">{p.rotulo}</dt>
                <dd className="numero text-right text-tinta">{formatarReais(p.valor)}</dd>
              </div>
            ))}
            <dt className="text-tinta-secundaria">Valor presente do caixa</dt>
            <dd className="numero text-right text-tinta">{a.valorPresente === null ? 'sem a taxa' : formatarReais(a.valorPresente)}</dd>
            <dt className="text-tinta-secundaria">Horas sem produzir</dt>
            <dd className="numero text-right text-tinta">{a.indisponibilidadeHoras === null ? '—' : horas(a.indisponibilidadeHoras)}</dd>
          </dl>
          {a.notas.length > 0 ? <ul className="txt-legenda flex list-disc flex-col gap-1 pl-4 text-pretty text-tinta-apagada">{a.notas.map((x) => <li key={x}>{x}</li>)}</ul> : null}
        </>
      ) : a.naoSeAplica ? (
        <p className="txt-apoio text-pretty text-tinta-secundaria">{`Não se aplica: ${a.naoSeAplica}.`}</p>
      ) : (
        <div className="flex items-start gap-2 rounded-controle bg-atencao-fundo p-3">
          <TriangleAlert aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-atencao" />
          <p className="txt-apoio text-pretty text-atencao-texto">{`Não calculada: falta ${a.falta.join(', ')}.`}</p>
        </div>
      )}
    </article>
  );
}

function Premissas({ e, aoEditar }: { readonly e: Estudo; readonly aoEditar: ((p: PremissaDoEstudo) => void) | null }) {
  return (
    <section className="flex flex-col gap-3" aria-label="As premissas">
      <h2 className="txt-secao text-tinta">Quais premissas a conta usa?</h2>
      {GRUPOS_DE_PREMISSA.map(([grupo, titulo]) => (
        <div key={grupo} className="cartao flex flex-col gap-2 p-4">
          <h3 className="txt-subtitulo text-tinta">{titulo}</h3>
          <ul className="flex flex-col divide-y divide-borda-sutil">
            {e.premissas.filter((p) => p.grupo === grupo).map((p) => (
              <li key={p.codigo} className="flex flex-wrap items-start justify-between gap-3 py-3">
                <div className="flex min-w-0 flex-[1_1_320px] flex-col gap-0.5">
                  <span className="txt-corpo text-tinta">{p.rotulo}</span>
                  <span className="txt-apoio text-pretty text-tinta-apagada">
                    {p.fonte ? `${p.fonte}${p.origem ? ` · ${NOME_DA_ORIGEM[p.origem]}` : ''}${p.demonstracao ? ' · Demonstração' : ''}` : `Onde entra: ${p.ondeEntra}`}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`txt-corpo numero whitespace-nowrap ${p.valor === null ? 'text-tinta-apagada' : 'text-tinta'}`}>{premissaEscrita(p)}</span>
                  {aoEditar && e.podeGravar ? (
                    <Botao nivel="fantasma" tamanho="p" icone aria-label={`Editar ${p.rotulo.toLowerCase()}`} title={`Editar ${p.rotulo.toLowerCase()}`} onClick={() => { aoEditar(p); }}>
                      <Pencil aria-hidden="true" className="size-4" />
                    </Botao>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}

function SerieMensal({ e }: { readonly e: Estudo }) {
  if (!e.serie) {
    return <p className="txt-apoio text-tinta-secundaria">O estudo concluído guarda os números da conclusão; o mês a mês de hoje está na ficha da máquina.</p>;
  }
  return (
    <section className="flex flex-col gap-3" aria-label="O mês a mês">
      <h2 className="txt-secao text-tinta">Como a máquina andou mês a mês?</h2>
      <Tabela
        rotulo="O histórico econômico mês a mês"
        colunas={[
          { chave: 'mes', rotulo: 'Mês', prioridade: 'chave', celula: (m) => <span className="flex flex-col"><span className="codigo text-tinta">{formatarCompetencia(m.competencia)}</span>{m.emAndamento ? <span className="txt-apoio text-atencao-texto">mês aberto</span> : null}</span> },
          { chave: 'horas', rotulo: 'Horas', alinhamento: 'numero', celula: (m) => <span className="numero text-tinta">{horas(m.horasProdutivas)}</span> },
          { chave: 'custo', rotulo: 'Custo total/h', alinhamento: 'numero', celula: (m) => <span className="numero text-tinta">{reaisPorHora(m.custoTotalPorHora)}</span> },
          { chave: 'manutencao', rotulo: 'Manutenção/h', alinhamento: 'numero', celula: (m) => <span className="numero text-tinta">{reaisPorHora(m.manutencaoPorHora)}</span> },
          { chave: 'margem', rotulo: 'Margem/h', alinhamento: 'numero', celula: (m) => <span className="numero text-tinta">{reaisPorHora(m.margemPorHora)}</span> },
          { chave: 'disp', rotulo: 'Disponib.', alinhamento: 'numero', celula: (m) => <span className="numero text-tinta">{percentual(m.disponibilidade)}</span> },
        ]}
        linhas={[...e.serie].reverse()}
        chaveDaLinha={(m) => m.competencia}
        alturaDupla
        vazio={<EstadoVazio titulo="Sem mês na janela" descricao="A máquina não teve custo nem hora nos 12 meses fechados." />}
        rodape={<RodapeDeContagem total={e.serie.length} />}
      />
      <p className="txt-apoio text-tinta-secundaria">{e.frases.mesAberto}</p>
      {e.faixas && e.faixas.faixas.length > 0 ? (
        <div className="cartao flex flex-col gap-2 p-4">
          <h3 className="txt-subtitulo text-tinta">Manutenção por hora em cada faixa de 500 h do horímetro</h3>
          <ul className="txt-apoio flex flex-col gap-1 text-tinta">
            {e.faixas.faixas.map((x) => (
              <li key={x.de} className="flex justify-between gap-3">
                <span>{`${horas(x.de)} a ${horas(x.ate)}`}</span>
                <span className="numero">{reaisPorHora(x.manutencaoPorHora)}</span>
              </li>
            ))}
          </ul>
          {e.faixas.semLeitura > 0 ? <p className="txt-legenda text-tinta-apagada">{`${String(e.faixas.semLeitura)} ${e.faixas.semLeitura === 1 ? 'mês sem leitura de horímetro ficou' : 'meses sem leitura de horímetro ficaram'} de fora`}</p> : null}
        </div>
      ) : null}
    </section>
  );
}

function JanelaDaPremissa({ e, p, aoFechar }: { readonly e: Estudo; readonly p: PremissaDoEstudo; readonly aoFechar: () => void }) {
  const cliente = useQueryClient();
  const [valor, setValor] = useState(p.valor === null ? '' : p.simOuNao ? String(p.valor) : String(p.valor).replace('.', ','));
  const [fonte, setFonte] = useState(p.origem === 'informada' ? (p.fonte ?? '') : '');
  const [tentou, setTentou] = useState(false);
  const numero = p.simOuNao ? (valor === '1' ? 1 : valor === '0' ? 0 : null) : interpretarNumero(valor);
  const faltaValor = numero === null || numero < 0 ? (p.simOuNao ? 'Escolha sim ou não.' : `Informe o valor em ${p.unidade}.`) : null;
  const faltaFonte = fonte.trim().length < 5 ? 'Escreva de onde vem o número, por exemplo "cotação da revenda de 02/10/2026".' : null;
  const gravar = useMutation({
    mutationFn: (remover: boolean) => apiDaRenovacao.gravarPremissa(e.estudo.id, p.codigo, remover ? null : numero, remover ? null : fonte),
    meta: { retorno: retornoDe<Awaited<ReturnType<typeof apiDaRenovacao.gravarPremissa>>>((r) => ({ titulo: tituloDaPremissa(r), ...detalheDaPremissa(r) })) },
    onSuccess: async () => {
      aoFechar();
      await cliente.invalidateQueries({ queryKey: ['renovacao'] });
    },
  });
  return (
    <Janela
      titulo={p.rotulo}
      descricao={`Onde entra: ${p.ondeEntra}. ${p.soInformada ? 'É premissa informada: o sistema não a calcula.' : 'Informada por você, ela vale no lugar da do sistema.'}`}
      aberta
      aoFechar={aoFechar}
      largura="p"
      acoes={
        <>
          {p.valor !== null ? <Botao nivel="fantasma" onClick={() => { gravar.mutate(true); }}>Remover premissa</Botao> : null}
          <Botao nivel="terciario" onClick={aoFechar}>Cancelar</Botao>
          <Botao nivel="primario" carregando={gravar.isPending} onClick={() => { setTentou(true); if (!faltaValor && !faltaFonte) gravar.mutate(false); }}>Gravar premissa</Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {gravar.isError ? <p role="alert" className="txt-apoio text-parada-texto">{`${explicarFalha(gravar.error).erro}. ${explicarFalha(gravar.error).comoResolver}`}</p> : null}
        {p.simOuNao ? (
          <CampoSelecao rotulo={p.rotulo} valor={valor} aoMudar={setValor} rotuloVazio="Escolha" opcoes={[{ valor: '1', rotulo: 'Sim' }, { valor: '0', rotulo: 'Não' }]} />
        ) : (
          <CampoTexto rotulo={`Valor (${p.unidade})`} valor={valor} aoMudar={setValor} placeholder="380.000,00" validar={() => (tentou ? faltaValor : null)} maxLength={20} />
        )}
        <CampoTexto rotulo="Fonte" valor={fonte} aoMudar={setFonte} placeholder="Cotação da revenda de 02/10/2026" ajuda="Premissa sem fonte é palpite: diga de onde vem o número." validar={() => (tentou ? faltaFonte : null)} maxLength={200} />
        {p.fonte && p.origem !== 'informada' ? <p className="txt-apoio text-tinta-secundaria">{`Hoje: ${premissaEscrita(p)} · ${p.fonte}`}</p> : null}
      </div>
    </Janela>
  );
}

function JanelaDaConclusao({ e, aberta, aoFechar }: { readonly e: Estudo; readonly aberta: boolean; readonly aoFechar: () => void }) {
  const cliente = useQueryClient();
  const calculadas = e.numeros.alternativas.filter((a) => a.calculada);
  const [alternativa, setAlternativa] = useState<Alternativa | ''>('');
  const [texto, setTexto] = useState('');
  const [tentou, setTentou] = useState(false);
  const faltaAlternativa = alternativa ? null : 'Escolha a alternativa que você recomenda.';
  const concluir = useMutation({
    mutationFn: () => apiDaRenovacao.concluir(e.estudo.id, alternativa as Alternativa, texto),
    meta: { retorno: retornoDe<Awaited<ReturnType<typeof apiDaRenovacao.concluir>>>((r) => ({ titulo: 'Estudo concluído', ...detalheDaConclusao(r) })) },
    onSuccess: async () => {
      aoFechar();
      await cliente.invalidateQueries({ queryKey: ['renovacao'] });
    },
  });
  return (
    <Janela
      titulo={`Concluir o ${codigoDoEstudo(e.estudo.numero)}`}
      descricao="A recomendação é sua. Ao concluir, os números de agora ficam congelados e o estudo não muda mais."
      aberta={aberta}
      aoFechar={aoFechar}
      largura="m"
      acoes={
        <>
          <Botao nivel="terciario" onClick={aoFechar}>Manter em estudo</Botao>
          <Botao nivel="primario" carregando={concluir.isPending} onClick={() => { setTentou(true); if (!faltaAlternativa && texto.trim().length >= 15) concluir.mutate(); }}>Concluir estudo</Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {concluir.isError ? <p role="alert" className="txt-apoio text-parada-texto">{`${explicarFalha(concluir.error).erro}. ${explicarFalha(concluir.error).comoResolver}`}</p> : null}
        {calculadas.length === 0 ? (
          <p className="txt-apoio text-atencao-texto">Nenhuma alternativa está calculada: informe as premissas que faltam antes de concluir.</p>
        ) : (
          <div className="flex flex-col gap-1">
            <CampoSelecao
              rotulo="Alternativa recomendada"
              valor={alternativa}
              aoMudar={(v) => { setAlternativa(v as Alternativa); }}
              rotuloVazio="Escolha a alternativa"
              opcoes={calculadas.map((a) => ({ valor: a.codigo, rotulo: `${a.nome} · ${a.custoPorHora === null ? formatarReais(a.total ?? 0) : reaisPorHora(a.custoPorHora)}` }))}
            />
            {tentou && faltaAlternativa ? <p className="txt-apoio text-parada-texto">{faltaAlternativa}</p> : null}
            <p className="txt-apoio text-tinta-secundaria">Só as alternativas calculadas podem ser escolhidas: a que não tem premissa não tem número.</p>
          </div>
        )}
        <CampoDoMotivo rotulo="Conclusão" valor={texto} aoMudar={setTexto} exemplo="Substituir em março: a reforma não cobre o material rodante" minimo={15} maximo={800} />
      </div>
    </Janela>
  );
}

function JanelaDoArquivo({ e, aberta, aoFechar }: { readonly e: Estudo; readonly aberta: boolean; readonly aoFechar: () => void }) {
  const cliente = useQueryClient();
  const [motivo, setMotivo] = useState('');
  const arquivar = useMutation({
    mutationFn: () => apiDaRenovacao.arquivar(e.estudo.id, motivo),
    meta: {
      retorno: retornoDe<Awaited<ReturnType<typeof apiDaRenovacao.arquivar>>>((r) => ({
        titulo: 'Estudo arquivado',
        tom: 'ressalva',
        alvo: { codigo: codigoDoEstudo(r.numero), nome: r.codigo },
        fatos: [r.motivo],
        efeito: 'A máquina pode ganhar um estudo novo',
        ver: '/equipamentos/renovacao',
      })),
    },
    onSuccess: async () => {
      aoFechar();
      await cliente.invalidateQueries({ queryKey: ['renovacao'] });
    },
  });
  return (
    <Janela
      titulo={`Arquivar o ${codigoDoEstudo(e.estudo.numero)}?`}
      descricao="O estudo sai de uso e fica na história, com o motivo. Ele não se apaga."
      aberta={aberta}
      aoFechar={aoFechar}
      largura="p"
      acoes={
        <>
          <Botao nivel="terciario" onClick={aoFechar}>Manter estudo</Botao>
          <Botao nivel="destrutivo" carregando={arquivar.isPending} disabled={!motivoValido(motivo)} onClick={() => { arquivar.mutate(); }}>Arquivar estudo</Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {arquivar.isError ? <p role="alert" className="txt-apoio text-parada-texto">{`${explicarFalha(arquivar.error).erro}. ${explicarFalha(arquivar.error).comoResolver}`}</p> : null}
        <CampoDoMotivo valor={motivo} aoMudar={setMotivo} exemplo="A máquina foi vendida antes da conclusão" />
      </div>
    </Janela>
  );
}

function JanelaDoOrcamento({ e, aberta, aoFechar }: { readonly e: Estudo; readonly aberta: boolean; readonly aoFechar: () => void }) {
  const cliente = useQueryClient();
  const [cenario, setCenario] = useState(e.estudo.cenarioId ?? '');
  const puxar = useMutation({
    mutationFn: () => apiDaRenovacao.puxarDoOrcamento(e.estudo.id, cenario || null),
    meta: {
      retorno: retornoDe<Awaited<ReturnType<typeof apiDaRenovacao.puxarDoOrcamento>>>((r) => ({
        titulo: 'Premissas puxadas do orçamento',
        ...(r.avisos.length > 0 ? { tom: 'ressalva' as const } : {}),
        alvo: { codigo: codigoDoEstudo(r.numero), nome: r.codigo },
        fatos: [`${String(r.preenchidas)} ${r.preenchidas === 1 ? 'premissa preenchida' : 'premissas preenchidas'}`, 'As informadas por você ficaram como estavam'],
        efeito: r.avisos[0] ?? { rotulo: 'Premissas do sistema e do orçamento', antes: String(r.substituidas), depois: String(r.preenchidas) },
        ver: `/equipamentos/renovacao/estudos/${r.id}`,
      })),
    },
    onSuccess: async () => {
      aoFechar();
      await cliente.invalidateQueries({ queryKey: ['renovacao'] });
    },
  });
  return (
    <Janela
      titulo="Puxar do orçamento"
      descricao="A demanda do tipo, o diesel do cenário e a taxa do custo de capital vêm da versão aprovada do orçamento. As premissas que você informou ficam como estão."
      aberta={aberta}
      aoFechar={aoFechar}
      largura="p"
      acoes={
        <>
          <Botao nivel="terciario" onClick={aoFechar}>Cancelar</Botao>
          <Botao nivel="primario" carregando={puxar.isPending} onClick={() => { puxar.mutate(); }}>Puxar premissas</Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {puxar.isError ? <p role="alert" className="txt-apoio text-parada-texto">{`${explicarFalha(puxar.error).erro}. ${explicarFalha(puxar.error).comoResolver}`}</p> : null}
        {e.orcamento.versao ? (
          <>
            <p className="txt-apoio text-tinta-secundaria">{`Orçamento aprovado: ${e.orcamento.versao.nome}, versão ${String(e.orcamento.versao.versao)}.`}</p>
            <CampoSelecao rotulo="Cenário" valor={cenario} aoMudar={setCenario} rotuloVazio="O primeiro cenário da versão" opcoes={e.orcamento.cenarios.map((c) => ({ valor: c.id, rotulo: c.nome }))} />
          </>
        ) : (
          <p className="txt-apoio text-atencao-texto">O exercício não tem orçamento aprovado: só o preço médio do diesel e as horas da máquina serão preenchidos.</p>
        )}
      </div>
    </Janela>
  );
}

